"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Mail, ShoppingBag } from "lucide-react"

import { useCart } from "@/lib/cart-store"

/**
 * Mobile-only sticky bottom CTA for /produit/[slug].
 *
 * Why: B2B mobile traffic is now the dominant first touch for ADIS 95
 * buyers (45-y-o menuisier on a 6-inch screen during a coffee break).
 * The in-page `ProductCartActions` block sits in the right-column
 * sidebar which on mobile slides BELOW the image carousel and the
 * spec table — meaning the moment the buyer scrolls down to read
 * specs, the "Add to cart" / "Buy now" button is off-screen.
 *
 * This component pins a single decisive action to the bottom of the
 * viewport from the moment the in-page CTA scrolls out of view, so
 * any scroll position is one tap from a purchase commitment.
 *
 * Behaviour:
 *   - Visible only on mobile (`md:hidden`).
 *   - Hidden until `watchTargetId` (the in-page CTA block) leaves
 *     viewport. We use IntersectionObserver, never a scroll listener,
 *     so it's idle when the page isn't being scrolled.
 *   - Two modes:
 *       • product has a price → "Acheter" pill that adds 1 unit and
 *         navigates straight to `/panier/commander` (matches the in-
 *         page Buy Now flow).
 *       • product has no price (devis-only) → "Demander un devis"
 *         pill linking to /contact?product=<slug>.
 *   - Cart store + router are the same as ProductCartActions, so the
 *     login-bounce-then-return flow works identically.
 */

export type ProductStickyCtaProps = {
  id: string
  slug: string
  name: string
  brand?: string
  image?: string
  /** HT euro price. `null` ⇒ no Stripe path; render the devis CTA. */
  unitPrice: number | null
  /** DOM id of the in-page CTA block; the sticky bar hides while
   *  this element is on screen so we don't show two CTAs at once. */
  watchTargetId: string
}

const eur = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
})

export function ProductStickyCta({
  id,
  slug,
  name,
  brand,
  image,
  unitPrice,
  watchTargetId,
}: ProductStickyCtaProps) {
  const router = useRouter()
  const { add } = useCart()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const target = document.getElementById(watchTargetId)
    if (!target) {
      // Fallback: show after the first viewport-and-a-half of scroll
      // so we still appear if the in-page CTA wasn't tagged.
      const onScroll = () => {
        setVisible(window.scrollY > window.innerHeight * 0.6)
      }
      onScroll()
      window.addEventListener("scroll", onScroll, { passive: true })
      return () => window.removeEventListener("scroll", onScroll)
    }
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      // Trigger only when the in-page CTA is fully out of view (give
      // it a 40 % bottom slack so we don't flicker during natural
      // scroll-stops).
      { rootMargin: "0px 0px -40% 0px", threshold: 0 },
    )
    obs.observe(target)
    return () => obs.disconnect()
  }, [watchTargetId])

  if (!visible) return null

  const hasPrice = typeof unitPrice === "number" && unitPrice > 0

  function handleBuy() {
    if (!hasPrice) return
    add({
      id,
      slug,
      name,
      brand,
      image,
      unitPrice: unitPrice as number,
      quantity: 1,
    })
    router.push("/panier/commander")
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
      role="region"
      aria-label="Action rapide produit"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          {brand ? (
            <p className="truncate text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              {brand}
            </p>
          ) : null}
          {hasPrice ? (
            <p className="text-base font-bold tabular-nums">
              {eur.format(unitPrice as number)}{" "}
              <span className="text-[11px] font-normal text-muted-foreground">
                HT
              </span>
            </p>
          ) : (
            <p className="text-sm font-semibold text-foreground">
              Prix sur demande
            </p>
          )}
        </div>
        {hasPrice ? (
          <button
            type="button"
            onClick={handleBuy}
            className="inline-flex h-12 min-w-[44px] items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-transform duration-150 active:scale-[0.97]"
          >
            <ShoppingBag className="size-4" aria-hidden="true" />
            Acheter
          </button>
        ) : (
          <Link
            href={`/contact?product=${encodeURIComponent(slug)}`}
            className="inline-flex h-12 min-w-[44px] items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-transform duration-150 active:scale-[0.97]"
          >
            <Mail className="size-4" aria-hidden="true" />
            Contact
          </Link>
        )}
      </div>
    </div>
  )
}
